import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Permission } from '@customerio/hydra-permissions';
import { getVariantReadFlags, type StartingPointVariant } from './steps';
import { useSampleWorkspace } from '@/features/workspaces';
import { useCampaignsOptional } from '@/services/db/campaign/queries';
import { getNewslettersQueryOptions } from '@/services/db/newsletter/queries';
import { getSegmentsQueryOptions } from '@/services/db/segment/queries';
import { getTransactionalMessagesQueryOptions } from '@/services/db/transactional-message/queries';

/** Exported so tests can advance a faked clock by exactly one poll. */
export const VARIANT_ASSET_POLL_INTERVAL_MS = 5_000;
/**
 * Bounds the liveness window to ~30 minutes of 5s polls (failed fetches count
 * too, so a persistently erroring list can't refetch forever). After the cap,
 * the step still updates through staleTime-0 remounts and the poll restarts
 * with the next mount.
 */
const MAX_POLLS = 360;

type UseVariantAssetPollOptions = {
  variant: StartingPointVariant;
  /** While the answers read is pending the variant is a guess; nothing polls. */
  isVariantPending: boolean;
  workspaceId: number | null;
  hasPermission: (permission: Permission) => boolean;
  /** The step's derived completion; polling stops the moment it flips. */
  isComplete: boolean;
};

/**
 * Polls the variant's list reads while the step is incomplete: the MCP and
 * chat paths create the asset outside this tab, and the banner and rail must
 * flip without a remount. Same query keys and enable conditions as the
 * completion signal, so a source the user can't view is never polled.
 * Background polling because the window is unfocused exactly while the
 * external tool does the work. Never polls the read-only sample workspace,
 * whose seeded assets can't complete the step anyway.
 */
export function useVariantAssetPoll({
  variant,
  isVariantPending,
  workspaceId,
  hasPermission,
  isComplete,
}: UseVariantAssetPollOptions) {
  const queryClient = useQueryClient();
  const { isSampleWorkspace, isLoading: isSampleLoading, isError: isSampleError } = useSampleWorkspace();
  const hasWorkspace = workspaceId != null && workspaceId > 0;
  const reads = getVariantReadFlags(variant, isVariantPending, hasPermission);
  const isPollable = !isComplete && !isSampleLoading && !isSampleError && !isSampleWorkspace(workspaceId);

  // Generic over the query so one callback fits every read's own query type.
  const refetchInterval = <Q extends { state: { dataUpdateCount: number; errorUpdateCount: number } }>(
    query: Q,
  ): number | false => {
    if (!isPollable) {
      return false;
    }
    const pollCount = query.state.dataUpdateCount + query.state.errorUpdateCount;
    return pollCount >= MAX_POLLS ? false : VARIANT_ASSET_POLL_INTERVAL_MS;
  };
  const keyWorkspaceId = workspaceId ?? 0;

  useCampaignsOptional({
    workspaceId: reads.campaigns ? workspaceId : null,
    params: variant === 'broadcasts' ? { page: 1, type: 'triggered_broadcast' } : { page: 1 },
    queryConfig: { staleTime: 0 },
    refetchInterval,
    refetchIntervalInBackground: true,
  });
  useQuery({
    ...getNewslettersQueryOptions(keyWorkspaceId, queryClient),
    staleTime: 0,
    enabled: reads.newsletters && hasWorkspace,
    refetchInterval,
    refetchIntervalInBackground: true,
  });
  useQuery({
    ...getTransactionalMessagesQueryOptions(keyWorkspaceId, queryClient),
    staleTime: 0,
    enabled: reads.transactionalMessages && hasWorkspace,
    refetchInterval,
    refetchIntervalInBackground: true,
  });
  useQuery({
    ...getSegmentsQueryOptions(keyWorkspaceId, queryClient),
    staleTime: 0,
    enabled: reads.segments && hasWorkspace,
    refetchInterval,
    refetchIntervalInBackground: true,
  });
}
